import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import TextBox, { type TextBoxRef } from 'devextreme-react/text-box';
import { useResponsivePopupOptions } from '../shell/useResponsivePopup';
import { getNewFileStarter } from './fileTypeRegistry';
import { normalizeStarterFileName } from './fileBrowserNewFile';
import './FileBrowserNewFilePromptPopup.css';

export type FileBrowserNewFilePromptPopupProps = {
  visible: boolean;
  starterId: string | null;
  onClose: () => void;
  onCreate: (fileName: string) => void;
};

export function FileBrowserNewFilePromptPopup({
  visible,
  starterId,
  onClose,
  onCreate
}: FileBrowserNewFilePromptPopupProps) {
  const starter = useMemo(() => (starterId ? getNewFileStarter(starterId) : null), [starterId]);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<TextBoxRef>(null);
  const popupOptions = useResponsivePopupOptions({ width: 380, height: 'auto' });

  useEffect(() => {
    if (!visible || !starter) return;
    setName(starter.defaultName);
    setError(null);
    const timer = window.setTimeout(() => {
      const instance = inputRef.current?.instance();
      instance?.focus();
      const el = instance?.element()?.querySelector('input');
      el?.select();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [visible, starter]);

  const submit = useCallback(() => {
    if (!starter) return;
    try {
      const normalized = normalizeStarterFileName(name, starter);
      setError(null);
      onCreate(normalized);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid file name.');
    }
  }, [starter, name, onCreate]);

  const title = starter ? `New ${starter.label}` : 'New file';

  return (
    <Popup
      visible={visible && starterId != null}
      onHiding={onClose}
      showTitle
      title={title}
      showCloseButton
      dragEnabled={false}
      hideOnOutsideClick
      {...popupOptions}
      wrapperAttr={{ class: 'jarvis-new-file-prompt-popup' }}
      position={{ my: 'center', at: 'center', of: window }}
    >
      <div className="jarvis-new-file-prompt">
        <label className="jarvis-new-file-prompt__label" htmlFor="jarvis-new-file-name">
          File name
        </label>
        <TextBox
          ref={inputRef}
          inputAttr={{ id: 'jarvis-new-file-name' }}
          value={name}
          valueChangeEvent="input"
          stylingMode="outlined"
          onValueChanged={(e) => {
            setName(String(e.value ?? ''));
            if (error) setError(null);
          }}
          onEnterKey={submit}
        />
        {error ? (
          <div className="jarvis-new-file-prompt__error" role="alert">
            {error}
          </div>
        ) : (
          <div className="jarvis-new-file-prompt__hint">
            {starter ? `Extension ${starter.extension} is added automatically if omitted.` : null}
          </div>
        )}
        <div className="jarvis-new-file-prompt__actions">
          <Button text="Cancel" stylingMode="text" onClick={onClose} />
          <Button text="Create" type="default" stylingMode="contained" onClick={submit} />
        </div>
      </div>
    </Popup>
  );
}
